import { useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { DownloadSimple, UploadSimple, Trash, Warning, Database } from '@phosphor-icons/react';

const API = '/api';

export default function DataManagementPanel() {
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [purgeLoading, setPurgeLoading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoreMode, setRestoreMode] = useState('replace');
  const [purgeConfirm, setPurgeConfirm] = useState('');
  const [showPurgeDialog, setShowPurgeDialog] = useState(false);

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const res = await axios.get(`${API}/admin/backup`, { withCredentials: true, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      a.download = `RootTrace_Backup_${timestamp}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      alert('Backup failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setBackupLoading(false);
    }
  };

  const handleRestoreUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setRestoreFile(file);
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = JSON.parse(evt.target.result);
        const collections = {};
        if (data.data) {
          Object.entries(data.data).forEach(([k, v]) => {
            collections[k] = Array.isArray(v) ? v.length : 0;
          });
        }
        setRestorePreview({ meta: data.meta, collections });
      } catch {
        alert('Invalid backup file format');
        setRestoreFile(null);
      }
    };
    reader.readAsText(file);
  };

  const handleRestoreConfirm = async () => {
    if (!restoreFile) return;
    setRestoreLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', restoreFile);
      formData.append('mode', restoreMode);
      await axios.post(`${API}/admin/restore`, formData, { withCredentials: true });
      alert('Data restored successfully!');
      setRestoreFile(null);
      setRestorePreview(null);
      window.location.reload();
    } catch (e) {
      alert('Restore failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRestoreLoading(false);
    }
  };

  const handlePurge = async () => {
    if (purgeConfirm !== 'DELETE ALL') return;
    setPurgeLoading(true);
    try {
      await axios.delete(`${API}/admin/purge`, { withCredentials: true });
      alert('All data has been permanently deleted.');
      setShowPurgeDialog(false);
      setPurgeConfirm('');
      window.location.reload();
    } catch (e) {
      alert('Purge failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setPurgeLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="data-management-panel">
      {/* Backup */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
            <Database size={24} className="text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Backup & Restore</h3>
            <p className="text-sm text-slate-500 mt-1">Download a complete backup of all your RootTrace data or restore from a previous backup.</p>
            <div className="flex gap-3 mt-4">
              <Button onClick={handleBackup} disabled={backupLoading} className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg" data-testid="backup-btn">
                <DownloadSimple size={18} className="mr-2" />
                {backupLoading ? 'Creating Backup...' : 'Download Backup'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Restore */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
            <UploadSimple size={24} className="text-emerald-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit' }}>Restore Data</h3>
            <p className="text-sm text-slate-500 mt-1">Upload a backup file to restore your data.</p>
            <div className="mt-4 space-y-3">
              <div className="flex items-center gap-4">
                <Select value={restoreMode} onValueChange={setRestoreMode}>
                  <SelectTrigger className="w-48 rounded-lg" data-testid="restore-mode-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="replace">Replace All</SelectItem>
                    <SelectItem value="merge">Merge (Add New)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <input
                  type="file"
                  accept=".json"
                  onChange={handleRestoreUpload}
                  className="block w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                  data-testid="restore-file-input"
                />
              </div>

              {restorePreview && (
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <h4 className="text-sm font-semibold text-slate-800 mb-2">Backup Preview</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs">
                    <div className="text-slate-500">Exported: <span className="text-slate-800 font-medium">{restorePreview.meta?.exported_at?.split('T')[0]}</span></div>
                    <div className="text-slate-500">By: <span className="text-slate-800 font-medium">{restorePreview.meta?.exported_by}</span></div>
                    <div className="text-slate-500">Documents: <span className="text-slate-800 font-medium">{restorePreview.meta?.total_documents}</span></div>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {Object.entries(restorePreview.collections || {}).map(([name, count]) => (
                      <span key={name} className="px-2 py-1 bg-white border border-slate-200 rounded text-xs text-slate-600">
                        {name}: <span className="font-bold text-slate-800">{count}</span>
                      </span>
                    ))}
                  </div>
                  <Button
                    className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
                    onClick={handleRestoreConfirm}
                    disabled={restoreLoading}
                    data-testid="restore-confirm-btn"
                  >
                    <UploadSimple size={18} className="mr-2" />
                    {restoreLoading ? 'Restoring...' : `Confirm Restore (${restoreMode})`}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Purge */}
      <div className="bg-white rounded-xl border border-red-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center shrink-0">
            <Trash size={24} className="text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-red-900" style={{ fontFamily: 'Outfit' }}>Delete All Data</h3>
            <p className="text-sm text-red-600 mt-1">
              Permanently delete all investigations, tasks, evidence, incidents, inventory, and all other data.
              Only your admin account will be preserved. This action cannot be undone.
            </p>
            <Button
              variant="outline"
              className="mt-4 border-red-300 text-red-600 hover:bg-red-50 rounded-lg"
              onClick={() => setShowPurgeDialog(true)}
              data-testid="purge-open-btn"
            >
              <Trash size={18} className="mr-2" />
              Delete All Data
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={showPurgeDialog} onOpenChange={setShowPurgeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Warning size={24} weight="fill" />
              Confirm Data Deletion
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-700">
              This will permanently delete ALL data. Type <strong className="text-red-600">DELETE ALL</strong> to confirm.
            </p>
            <input
              type="text"
              value={purgeConfirm}
              onChange={(e) => setPurgeConfirm(e.target.value)}
              placeholder="Type DELETE ALL"
              className="w-full px-3 py-2 border border-red-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid="purge-confirm-input"
            />
            <div className="flex gap-3">
              <Button
                onClick={handlePurge}
                disabled={purgeConfirm !== 'DELETE ALL' || purgeLoading}
                className="bg-red-600 hover:bg-red-700 text-white rounded-lg"
                data-testid="purge-confirm-btn"
              >
                {purgeLoading ? 'Deleting...' : 'Permanently Delete All Data'}
              </Button>
              <Button variant="outline" onClick={() => { setShowPurgeDialog(false); setPurgeConfirm(''); }} className="rounded-lg">Cancel</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
